import classnames from 'classnames';
import { useMemo } from 'react';
import Carousel from '@/common/Carousel/Carousel';
import { getHomeHref as getHref } from '@/common/SearchBar/utils';
import TrackWrapper from '@/common/TrackWrapper';
import formatEvent from '@/home/utils/formatEvent';
import homeFeedBodyStyles from '~/constants/home/bodystylesList';
import { useIsMobile } from '~/context/common/DeviceContext';
import type { BodyStyle } from '~/contracts/home';
import useCarouselSettings from '~/hooks/home/useCarouselSettings';
import { nativeSrpPayload } from '~/native/home/utils';
import {
  GridWrapper,
  CarouselWrapper,
  InnerWrapper,
  SectionTitle,
  BodyStyleWrapper,
  BodyStyleLabel
} from './PopularBodyStyles.styles';

type OwnProps = {
  isReturningOrLoggedInUser: boolean;
};

type BodyStyleCardProps = {
  bodyStyle: BodyStyle;
  isShopAllCard?: boolean;
  className?: string;
};

const bodyStylesWithShopAll = [
  ...homeFeedBodyStyles,
  {
    alt: 'Shop all',
    image: `${process.env.CVNA_APP_CDN_URL}/home-assets/icons/ShopAllIcon.svg?width=75&height=75`,
    tabletImage: `${process.env.CVNA_APP_CDN_URL}/home-assets/icons/ShopAllIcon.svg?width=45&height=45`,
    mobileImage: `${process.env.CVNA_APP_CDN_URL}/home-assets/icons/ShopAllIcon.svg?width=45&height=45`,
    link: '/cars'
  }
];

const BodyStyleCard = ({
  bodyStyle,
  isShopAllCard = false,
  className = ''
}: BodyStyleCardProps) => {
  return (
    <a
      href={getHref(bodyStyle.link)}
      key={bodyStyle.alt}
      data-analytics-id="Home feed card"
      data-analytics-attributes={JSON.stringify({
        description: 'Popular body style tile',
        content: bodyStyle.alt
      })}
      className="inline-block"
      data-native-payload={nativeSrpPayload}
    >
      <BodyStyleWrapper
        className={classnames(
          {
            'hidden h-full m:flex m:gap-8': isShopAllCard
          },
          className
        )}
      >
        <picture
          className={classnames('w-full, h-full', {
            'mt-auto flex items-center justify-center rounded-full bg-grey-1 m:h-[55px] m:w-[55px] l:h-[75px] l:w-[75px]':
              isShopAllCard
          })}
        >
          <source media="(min-width: 1024px)" srcSet={bodyStyle.image} />
          <source media="(min-width: 768px)" srcSet={bodyStyle.tabletImage} />
          <img src={bodyStyle.mobileImage} alt={bodyStyle.alt} />
        </picture>
        <BodyStyleLabel>{bodyStyle.alt}</BodyStyleLabel>
      </BodyStyleWrapper>
    </a>
  );
};

export default function PopularBodyStyles({
  isReturningOrLoggedInUser
}: OwnProps) {
  const isMobile = useIsMobile();

  const viewEvent = useMemo(() => {
    const customAttributes = {
      description: 'Popular body styles',
      isCarousel: !isReturningOrLoggedInUser && isMobile ? true : false,
      content: isMobile
        ? homeFeedBodyStyles.map(bodyStyle => bodyStyle.alt)
        : bodyStylesWithShopAll.map(bodyStyle => bodyStyle.alt)
    };

    return formatEvent('Viewed - Home feed section', customAttributes);
  }, [isMobile, isReturningOrLoggedInUser]);

  const { values, carouselRef } = useCarouselSettings({
    maxLength: homeFeedBodyStyles.length,
    speed: 1000,
    slidesToScroll: 2,
    widthGap: 115
  });

  return (
    <TrackWrapper event={viewEvent}>
      <InnerWrapper>
        <SectionTitle>Popular body styles</SectionTitle>
        <GridWrapper
          className={classnames('hidden', {
            '!grid': isReturningOrLoggedInUser
          })}
        >
          <>
            {bodyStylesWithShopAll.map(bodyStyle => {
              const isShopAllCard = bodyStyle.alt === 'Shop all';

              return (
                <BodyStyleCard
                  key={bodyStyle.alt}
                  bodyStyle={bodyStyle}
                  isShopAllCard={isShopAllCard}
                  className={classnames({
                    'border-grey-2 m:border': !isReturningOrLoggedInUser
                  })}
                />
              );
            })}
          </>
        </GridWrapper>
        <CarouselWrapper
          className={classnames('block m:!hidden', {
            '!hidden': isReturningOrLoggedInUser
          })}
        >
          <Carousel {...values} ref={carouselRef}>
            {homeFeedBodyStyles.map(bodyStyle => {
              return (
                <BodyStyleCard
                  key={bodyStyle.alt}
                  bodyStyle={bodyStyle}
                  className={classnames('!w-[104px]', {
                    '!bg-grey-1': !isReturningOrLoggedInUser
                  })}
                />
              );
            })}
          </Carousel>
        </CarouselWrapper>
      </InnerWrapper>
    </TrackWrapper>
  );
}
